import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import BusinessIcon from '@mui/icons-material/Business';
import { adminApi } from '../../api/admin';
// Using native JavaScript date formatting

const OperationsPanel: React.FC = () => {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmOpen, setConfirmOpen] = useState<string | null>(null);
  const [lastOperation, setLastOperation] = useState<any>(null);

  const handleOperation = async (operation: 'startOfDay' | 'endOfDay' | 'closeOfBusiness') => {
    setLoading(operation);
    setError('');
    setSuccess('');

    try {
      let result;
      switch (operation) {
        case 'startOfDay':
          result = await adminApi.startOfDay();
          break;
        case 'endOfDay':
          result = await adminApi.endOfDay();
          break;
        case 'closeOfBusiness':
          result = await adminApi.closeOfBusiness();
          break;
      }
      setLastOperation(result);
      setSuccess(result.message || 'Operation completed successfully');
      setConfirmOpen(null);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to perform operation');
    } finally {
      setLoading(null);
    }
  };

  const getOperationName = (operation: string) => {
    switch (operation) {
      case 'startOfDay':
        return 'Start of Day';
      case 'endOfDay':
        return 'End of Day';
      case 'closeOfBusiness':
        return 'Close of Business';
      default:
        return operation;
    }
  };

  const getOperationDescription = (operation: string) => {
    switch (operation) {
      case 'startOfDay':
        return 'Perform start of day operations including system checks and validations.';
      case 'endOfDay':
        return 'Perform end of day operations including daily reports and reconciliations.';
      case 'closeOfBusiness':
        return 'Perform close of business operations including final reconciliations and backups.';
      default:
        return '';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Daily Operations
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {lastOperation && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Last Operation: {lastOperation.message} at{' '}
          {lastOperation.timestamp
            ? new Date(lastOperation.timestamp).toLocaleString()
            : 'N/A'}
          {lastOperation.performedBy && ` by ${lastOperation.performedBy}`}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PlayArrowIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Typography variant="h6">Start of Day</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {getOperationDescription('startOfDay')}
              </Typography>
              <Button
                variant="contained"
                color="success"
                fullWidth
                onClick={() => setConfirmOpen('startOfDay')}
                disabled={loading !== null}
                startIcon={
                  loading === 'startOfDay' ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
              >
                {loading === 'startOfDay' ? 'Processing...' : 'Start of Day'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StopIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Typography variant="h6">End of Day</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {getOperationDescription('endOfDay')}
              </Typography>
              <Button
                variant="contained"
                color="warning"
                fullWidth
                onClick={() => setConfirmOpen('endOfDay')}
                disabled={loading !== null}
                startIcon={
                  loading === 'endOfDay' ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <StopIcon />
                  )
                }
              >
                {loading === 'endOfDay' ? 'Processing...' : 'End of Day'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                <Typography variant="h6">Close of Business</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {getOperationDescription('closeOfBusiness')}
              </Typography>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={() => setConfirmOpen('closeOfBusiness')}
                disabled={loading !== null}
                startIcon={
                  loading === 'closeOfBusiness' ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <BusinessIcon />
                  )
                }
              >
                {loading === 'closeOfBusiness' ? 'Processing...' : 'Close of Business'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={confirmOpen !== null} onClose={() => setConfirmOpen(null)}>
        <DialogTitle>Confirm Operation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to perform{' '}
            <strong>{confirmOpen ? getOperationName(confirmOpen) : ''}</strong>?
            <br />
            <br />
            {confirmOpen && getOperationDescription(confirmOpen)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(null)}>Cancel</Button>
          <Button
            onClick={() => confirmOpen && handleOperation(confirmOpen as any)}
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperationsPanel;

